import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Button, Select, DatePicker, Space } from "antd";
import {
  ExpandOutlined,
  EditOutlined,
  ShareAltOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { SimpleTagManager } from "./SimpleTagManager";

const { RangePicker } = DatePicker;

const iterationTypes = [
  { value: "SPRINT", label: "Sprint" },
  { value: "QUARTER", label: "Quarter" },
];

const CreateIterationDrawer = ({
  open,
  onClose,
  onSubmit,
  projects = [],
  availableTags = [],
  mode = "create",
  initialValues = null,
  loading,
  onTagsUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localTags, setLocalTags] = useState(initialValues?.tags || []);
  const [form] = Form.useForm();

  useEffect(() => {
    if (mode === "edit" && initialValues) {
      form.setFieldsValue({
        ...initialValues,

        dateRange:
          initialValues.startDate && initialValues.endDate
            ? [dayjs(initialValues.startDate), dayjs(initialValues.endDate)]
            : null,

        tags: initialValues.tags?.map((tag) => tag.id) || [],
      });
      setLocalTags(initialValues.tags || []);
    }

    if (mode === "create") {
      form.resetFields();
    }
  }, [mode, initialValues, form]);

  const handleFinish = (values) => {
    const payload = {
      ...values,
      startDate: values.dateRange?.[0]?.format("YYYY-MM-DD"),
      endDate: values.dateRange?.[1]?.format("YYYY-MM-DD"),
    };

    onSubmit?.(payload);
    if (mode === "create") {
      form.resetFields();
      delete payload.dateRange;
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  return (
    <Drawer
      size={isExpanded ? "100%" : 520}
      zIndex={9999}
      open={open}
      onClose={handleClose}
      destroyOnHidden
      closable={false}
      footer={
        <div
          style={{
            textAlign: "right",
            maxWidth: isExpanded ? 700 : "100%",
            margin: isExpanded ? "0 auto" : 0,
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            {mode === "edit" ? "Save Changes" : "Create"}
          </Button>
        </div>
      }
    >
      <div
        style={{
          maxWidth: isExpanded ? 700 : "100%",
          margin: isExpanded ? "0 auto" : 0,
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* Breadcrumb / page context */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ cursor: "pointer" }}>Project</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {mode === "edit" ? "Edit Iteration" : "Create Iteration"}
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            <ShareAltOutlined className="task-drawer-icon" />
            <MoreOutlined className="task-drawer-icon" />
          </div>
        </div>

        <Form layout="vertical" form={form} onFinish={handleFinish}>
          {/* Type */}
          <Form.Item
            name="type"
            label="Iteration Type"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select type" options={iterationTypes} />
          </Form.Item>

          {/* Key */}
          <Form.Item
            name="key"
            label="Iteration Key"
            rules={[{ required: true }]}
          >
            <Input placeholder="e.g. SPRINT-12 or 2025-Q4" />
          </Form.Item>

          {/* Name */}
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Sprint 12" />
          </Form.Item>

          {/* Target points */}
          {mode === "create" && (
            <Form.Item
              name="targetPoints"
              label="Target Points"
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter estimate points" />
            </Form.Item>
          )}

          {/* Date Range */}
          <Form.Item name="dateRange" label="Start & End Date">
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>

          {/* Tags */}
          {mode === "create" && (
            <Form.Item name="tags" label="Tags">
              <Select
                mode="multiple"
                placeholder="Select tags"
                maxTagCount={isExpanded ? 9 : 4}
                options={availableTags.map((tag) => ({
                  value: tag.id,
                  label: tag.name,
                }))}
              />
            </Form.Item>
          )}

          {mode === "edit" && (
            <>
              <label style={{ color: "#555" }}>Tags</label>
              <SimpleTagManager
                entityType="iteration"
                entityId={initialValues?.id}
                projectId={initialValues?.projectId}
                currentTags={localTags}
                availableTags={availableTags}
                onChange={(updatedTags) => {
                  setLocalTags(updatedTags);
                  onTagsUpdate?.(initialValues.id, updatedTags);
                }}
              />
            </>
          )}
        </Form>
      </div>
    </Drawer>
  );
};

export default CreateIterationDrawer;
